import { z } from "zod";

/**
 * Ways to sign in. One person is one `user`; every way they sign in is an `account` row attached to it (Better Auth):
 * `credential` is e-mail + password, `apple` is Sign in with Apple, `google` is Google. Whichever way someone signs in,
 * on whichever platform, they reach the same account — and so the same weddings and the same plan.
 *
 * The API enforces every rule (apps/api/src/auth/); clients only decide what to show. The reasoning and the store
 * rules behind it: docs/research/2026-10-one-account-plan.md §3.
 */
export const SOCIAL_PROVIDERS = ["apple", "google"] as const;
export type SocialProvider = (typeof SOCIAL_PROVIDERS)[number];

/** Better Auth's provider id for each sign-in method an account can hold. */
export type SignInMethod = "credential" | SocialProvider;

export function isSocialProvider(v: unknown): v is SocialProvider {
  return typeof v === "string" && (SOCIAL_PROVIDERS as readonly string[]).includes(v);
}

/**
 * `GET /api/sign-in-methods` (public). A provider is offered only while the server holds its credentials, so a
 * client shows exactly the buttons that work.
 */
export interface SignInMethodsDTO {
  emailPassword: true;
  apple: boolean;
  google: boolean;
}

/**
 * Why a sign-in with Apple or Google ended without a session (or a link without a link). Better Auth answers a code:
 * `?error=<code>` on the error URL of a browser flow, `{ code, message }` from an ID-token sign-in, or the code the
 * app hand-off passes back. Every client maps it here and words the problem in its own strings.
 *
 * - `noAccount`: no Bridewell account uses this Apple ID / Google account yet — sign-ups go through the sign-up screen,
 *   where the Terms box is ticked (providers never create an account on the sign-in screen).
 * - `unconfirmedAccount`: an account with this address exists but was never confirmed, so it cannot be joined
 *   automatically (that is the guard against someone registering another person's address first).
 * - `linkedElsewhere`: this Apple ID / Google account already belongs to another Bridewell account.
 * - `cancelled`: the person backed out at Apple or Google.
 * - `failed`: anything else (an expired attempt, a provider outage).
 */
export type SocialSignInProblem = "noAccount" | "unconfirmedAccount" | "linkedElsewhere" | "cancelled" | "failed";

export function socialSignInProblem(code: string | null | undefined, message?: string | null): SocialSignInProblem | null {
  if (!code) return null;
  // The ID-token path reports the redirect flow's reason as its message under one generic code.
  const raw = code === "OAUTH_LINK_ERROR" && message ? message : code;
  const key = raw.trim().toLowerCase().replace(/[\s-]+/g, "_");
  switch (key) {
    case "signup_disabled":
      return "noAccount";
    case "account_not_linked":
    case "email_not_verified":
      return "unconfirmedAccount";
    case "account_already_linked_to_different_user":
    case "social_account_already_linked":
    case "account_ownership_conflict":
      return "linkedElsewhere";
    case "access_denied":
    case "user_cancelled_authorize":
    case "cancelled":
      return "cancelled";
    default:
      return "failed";
  }
}

/* ───────── app hand-off: sign-in through the system browser for the native apps ───────── */

/**
 * The Android app (and the Apple apps for Google) sign in with Apple or Google in the system browser, never in a web
 * view (RFC 8252). The app keeps a random `verifier`, opens `/api/auth/app-handoff/start` with its S256 `challenge`
 * (RFC 7636), and the browser comes back on `returnPath` — a verified https link (Android App Link, Apple associated
 * domain), so only Bridewell's own signed app receives it — carrying a one-time `code`. The app trades code + verifier
 * at `/api/auth/app-handoff/exchange` for a session of its own. No token ever travels on the `bridewell://` scheme.
 */
export const APP_HANDOFF = {
  /** The page on the web origin the browser returns to (`?code=` or `?error=`). */
  returnPath: "/auth/app-return",
  /** Better Auth endpoints (under the API's `/api/auth`) the apps call themselves. */
  ticketPath: "/app-handoff/ticket",
  exchangePath: "/app-handoff/exchange",
  /** A code is good for this long, once. */
  codeTtlSeconds: 120,
  /** A link ticket (the app's session vouching for a link started in the browser) is good for this long, once. */
  ticketTtlSeconds: 120,
} as const;

/** What the browser trip is for: sign in to an existing account, create one (Terms box ticked in the app), or add a method. */
export const APP_HANDOFF_INTENTS = ["signIn", "signUp", "link"] as const;
export type AppHandoffIntent = (typeof APP_HANDOFF_INTENTS)[number];

/** base64url of a SHA-256 digest: 43 characters. */
const S256 = /^[A-Za-z0-9_-]{43}$/;
/** RFC 7636 §4.1 code_verifier: 43–128 unreserved characters. */
const VERIFIER = /^[A-Za-z0-9._~-]{43,128}$/;

/** `GET /api/auth/app-handoff/start` (query). `ticket` is required for `link` and refused otherwise. */
export const appHandoffStartSchema = z
  .object({
    provider: z.enum(SOCIAL_PROVIDERS),
    challenge: z.string().regex(S256),
    intent: z.enum(APP_HANDOFF_INTENTS).default("signIn"),
    ticket: z.string().min(32).max(128).optional(),
  })
  .refine((q) => (q.intent === "link") === (q.ticket !== undefined), { message: "A link needs a ticket; nothing else takes one.", path: ["ticket"] });
export type AppHandoffStart = z.infer<typeof appHandoffStartSchema>;

/** `POST /api/auth/app-handoff/exchange` */
export const appHandoffExchangeSchema = z.object({
  code: z.string().min(32).max(128),
  verifier: z.string().regex(VERIFIER),
});
export type AppHandoffExchange = z.infer<typeof appHandoffExchangeSchema>;

/** `POST /api/auth/app-handoff/ticket` → `{ ticket }` — signed-in apps only, for the `link` intent. */
export interface AppHandoffTicketDTO {
  ticket: string;
}

/** The `start` URL an app opens (on the API origin). String building only: this package runs where `URL` may not. */
export function appHandoffStartUrl(apiOrigin: string, q: { provider: SocialProvider; challenge: string; intent?: AppHandoffIntent; ticket?: string }): string {
  const params: [string, string][] = [["provider", q.provider], ["challenge", q.challenge], ["intent", q.intent ?? "signIn"]];
  if (q.ticket) params.push(["ticket", q.ticket]);
  const query = params.map(([k, v]) => `${k}=${encodeURIComponent(v)}`).join("&");
  return `${apiOrigin.replace(/\/+$/, "")}/api/auth/app-handoff/start?${query}`;
}

/** What came back on the return link: a code to exchange, a method that was added, a problem to show, or nothing usable. */
export type AppHandoffReturn = { code: string } | { linked: SocialProvider } | { problem: SocialSignInProblem };

export function readAppHandoffReturn(url: string): AppHandoffReturn | null {
  const match = /^https?:\/\/[^/?#]+([^?#]*)(?:\?([^#]*))?/.exec(url);
  if (!match || match[1] !== APP_HANDOFF.returnPath) return null;
  const query = new Map<string, string>();
  for (const pair of (match[2] ?? "").split("&")) {
    const at = pair.indexOf("=");
    if (at <= 0) continue;
    try {
      query.set(decodeURIComponent(pair.slice(0, at)), decodeURIComponent(pair.slice(at + 1).replace(/\+/g, " ")));
    } catch {
      return null;
    }
  }
  const error = query.get("error");
  if (error) return { problem: socialSignInProblem(error) ?? "failed" };
  const linked = query.get("linked");
  if (isSocialProvider(linked)) return { linked };
  const code = query.get("code");
  return code && code.length >= 32 && code.length <= 128 ? { code } : null;
}
